import { ChangeEvent, useEffect, useState } from 'react';

import { TextArea } from '@trackman/web-shared-components';
import { ValidationType } from 'types/GlobalTypes';
import { useTranslation } from 'react-i18next';

type PropsType = {
  label: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  validationRules?: Array<ValidationType>;
  hint?: string;
  disabled?: boolean;
};

const AppTextarea = ({ label, name, value = '', validationRules = [], hint = '', onChange, disabled = false }: PropsType) => {
  const [isTouched, setIsTouchedState] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [initialValue, setInitialValue] = useState<string>(value);

  const { t } = useTranslation();

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsTouchedState(true);
    isValueInvalid(e.target.value);
    onChange(e);
  };

  const isValueInvalid = (value: string): Array<string | boolean> => {
    if (!isTouched) return [];

    const errors = validationRules.map((rule) => rule(value)).filter(Boolean);

    setIsInvalid(!!errors.length);

    if (errors.length) setErrorMessage(errors.map(t).join(', ') || t('invalid_value') || 'invalid value');
    if (!errors.length) setErrorMessage('');

    return errors;
  };

  // validate after is touched
  useEffect(() => {
    if (isTouched) isValueInvalid(value);
  }, [isTouched]);

  useEffect(() => {
    if (initialValue) setIsTouchedState(true);
  }, [initialValue]);

  return (
    <TextArea
      label={label}
      name={name || ''}
      value={value}
      error={isInvalid}
      hint={errorMessage || hint}
      onChange={(e) => handleChange(e)}
      disabled={disabled}
      inputProps={{
        className: `${isInvalid ? 'app-textarea--is-invalid' : ''}`,
        'data-testid': name ? `${name}-textarea` : 'app-textarea',
      }}
    />
  );
};

export default AppTextarea;
